import { useEffect, useMemo, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn, inputValueToUnixSeconds, unixSecondsToInputValue } from '@/lib';

import type { PropertyData } from '@/domain/entities/property-object';
import { PropertyStatus, type IProperty } from '@/domain/entities/property';
import { AreaValueField } from '@/components/ui/AreaValueField';
import { usePermissions } from '@/hooks/usePermissions';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  patchSelectedProperty,
  markPropertyClean,
  selectPropertyIsDirty,
  selectPropertyIsSaving,
  selectPropertySaveError,
  selectPropertyById,
} from '@/features/properties/propertiesSlice';
import { fetchPropertyById, updateProperty } from '@/features/properties/propertiesThunks';

const PROPERTY_STATUS_OPTIONS: Array<{ value: PropertyStatus; label: string }> = [
  { value: PropertyStatus.BuilderDraft, label: 'Builder draft' },
  { value: PropertyStatus.ReadyForHandover, label: 'Ready for handover' },
  { value: PropertyStatus.HandoverInitiated, label: 'Handover initiated' },
  { value: PropertyStatus.Owned, label: 'Owned' },
];

function ensureProperty(data: PropertyData): IProperty {
  const now = Math.floor(Date.now() / 1000);

  const base =
    data.property ??
    ({
      id: `tmp-${now}`,
      title: '',
      description: null,
      createdAt: now,
      updatedAt: now,
      typeName: 'Property',
    } as IProperty);

  return {
    ...base,
    address: base.address ?? {
      address: null,
      country: null,
      city: null,
      state: null,
      province: null,
      postalCode: null,
      addressVerificationLevel: undefined,
      mapsAddressObject: undefined,
    },
  };
}

export function PropertyTab(props: {
  data: PropertyData;
  setData: (next: PropertyData) => void;
  mode: 'create' | 'edit';
  propertyIdFromRoute?: string;
}) {
  const { data, setData, mode, propertyIdFromRoute } = props;
  const dispatch = useAppDispatch();
  const { isSuperAdmin } = usePermissions();

  // store-driven state (edit mode)
  const resolvedId = mode === 'edit' ? (propertyIdFromRoute ?? null) : (data.property?.id ?? null);
  const storeProperty = useAppSelector((s) => (resolvedId ? selectPropertyById(s, resolvedId) : null));

  // use store property in edit mode, otherwise use draft
  const property = useMemo(() => {
    return mode === 'edit' ? (storeProperty ?? ensureProperty(data)) : ensureProperty(data);
  }, [mode, storeProperty, data]);

  // stable id for dirty selector + save actions

  // to avoid “all green on first load”
  const [hasSavedOnce, setHasSavedOnce] = useState(false);
  const isDirty = useAppSelector((s) => (resolvedId ? selectPropertyIsDirty(s, resolvedId) : false));
  const isSaving = useAppSelector((s) => (resolvedId ? selectPropertyIsSaving(s, resolvedId) : false));
  const saveError = useAppSelector((s) => (resolvedId ? selectPropertySaveError(s, resolvedId) : null));

  useEffect(() => {
    if (mode === 'edit') setHasSavedOnce(false);
  }, [mode, resolvedId]);

  const patch = (p: Partial<IProperty>) => {
    if (mode === 'edit') {
      if (!resolvedId) return; // route id is the source of truth in edit mode
      dispatch(patchSelectedProperty({ id: resolvedId, patch: p }));
      return;
    }

    setData({
      ...data,
      property: {
        ...property,
        ...p,
        address: p.address ? { ...property.address, ...p.address } : property.address,
      },
    });
  };

  const patchAddress = (p: Partial<NonNullable<IProperty['address']>>) => {
    patch({ address: { ...property.address, ...p } });
  };

  const toNumOrUndef = (raw: string): number | undefined => {
    const s = raw.trim();
    if (s === '') return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  };

  const handleUpdate = async () => {
    if (mode !== 'edit') return;
    if (!property?.id) return;

    await dispatch(updateProperty({ id: property.id, patch: property })).unwrap();
    setHasSavedOnce(true);
  };

  const handleReset = async () => {
    if (mode !== 'edit') return;
    if (!resolvedId) return;

    // re-fetch latest from backend
    await dispatch(fetchPropertyById({ id: resolvedId })).unwrap();
    dispatch(markPropertyClean({ id: resolvedId }));
    setHasSavedOnce(false);
  };

  const cardClass = cn(
    mode === 'edit' && 'border-2',
    mode === 'edit' && isDirty && 'border-orange-500/70 ring-1 ring-orange-500/30',
    mode === 'edit' && !isDirty && hasSavedOnce && 'border-emerald-500/60 ring-1 ring-emerald-500/25',
  );

  if (mode === 'edit' && !propertyIdFromRoute) {
    return (
      <Card className="border-destructive border-2">
        <CardHeader>
          <CardTitle className="text-destructive">Invalid route</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          This page is in edit mode but no property ID was provided in the route.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cardClass}>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle>Property</CardTitle>

        {saveError ? <div className="text-destructive truncate text-sm">{saveError}</div> : null}

        {mode === 'edit' ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} disabled={!isDirty || isSaving}>
              Reset
            </Button>
            <Button size="sm" onClick={handleUpdate} disabled={!isDirty || isSaving}>
              Update
            </Button>
          </div>
        ) : null}
      </CardHeader>

      <CardContent className="space-y-6">
        {mode === 'edit' && isSuperAdmin ? (
          <div className="border-border bg-muted/20 grid gap-4 rounded-md border p-4 md:grid-cols-[1fr_auto] md:items-end">
            <div className="space-y-2">
              <Label>Property status</Label>
              <Select
                value={property.status ?? PropertyStatus.BuilderDraft}
                onValueChange={(status) => patch({ status: status as PropertyStatus })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-muted-foreground text-xs">Super admin override for legacy property records.</div>
            </div>

            {property.status === PropertyStatus.BuilderDraft ? (
              <Button type="button" variant="outline" onClick={() => patch({ status: PropertyStatus.Owned })}>
                Mark owned
              </Button>
            ) : null}
          </div>
        ) : null}

        {/* Title + Description */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              placeholder="e.g., Model A"
              value={property.title ?? ''}
              onChange={(e) => patch({ title: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Optional description"
              value={property.description ?? ''}
              onChange={(e) => patch({ description: e.target.value || null })}
            />
          </div>
        </div>

        {/* Address */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Address</Label>
            <Input
              placeholder="e.g., 123 Main Street"
              value={property.address?.address ?? ''}
              onChange={(e) => patchAddress({ address: e.target.value || null })}
            />
          </div>

          <div className="space-y-2">
            <Label>City</Label>
            <Input
              placeholder="e.g., Toronto"
              value={property.address?.city ?? ''}
              onChange={(e) => patchAddress({ city: e.target.value || null })}
            />
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <div className="space-y-2">
            <Label>State</Label>
            <Input
              placeholder="e.g., Ontario / NY"
              value={property.address?.state ?? ''}
              onChange={(e) => patchAddress({ state: e.target.value || null })}
            />
          </div>

          <div className="space-y-2">
            <Label>Province</Label>
            <Input
              placeholder="e.g., ON"
              value={property.address?.province ?? ''}
              onChange={(e) => patchAddress({ province: e.target.value || null })}
            />
          </div>

          <div className="space-y-2">
            <Label>Postal Code</Label>
            <Input
              placeholder="e.g., M5V 2T6"
              value={property.address?.postalCode ?? ''}
              onChange={(e) => patchAddress({ postalCode: e.target.value || null })}
            />
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Country</Label>
            <Input
              placeholder="e.g., Canada"
              value={property.address?.country ?? ''}
              onChange={(e) => patchAddress({ country: e.target.value || null })}
            />
          </div>

          <AreaValueField label="Lot Area" value={property.lotArea} onChange={(next) => patch({ lotArea: next })} />
        </div>

        {/* Numeric fields */}
        <div className="grid gap-6 md:grid-cols-5">
          <div className="space-y-2">
            <Label>Year Built</Label>
            <Input
              type="date"
              value={property.yearBuilt ? unixSecondsToInputValue(property.yearBuilt, 'date') : ''}
              onChange={(e) => patch({ yearBuilt: inputValueToUnixSeconds(e.target.value, 'date') })}
            />
          </div>

          <div className="space-y-2">
            <Label>Floors</Label>
            <Input
              inputMode="numeric"
              value={property.numFloors ?? ''}
              onChange={(e) => patch({ numFloors: toNumOrUndef(e.target.value) })}
            />
          </div>

          <div className="space-y-2">
            <Label>Bedrooms</Label>
            <Input
              inputMode="numeric"
              value={property.bedrooms ?? ''}
              onChange={(e) => patch({ bedrooms: toNumOrUndef(e.target.value) })}
            />
          </div>

          <div className="space-y-2">
            <Label>Bathrooms</Label>
            <Input
              inputMode="decimal"
              value={property.bathrooms ?? ''}
              onChange={(e) => patch({ bathrooms: toNumOrUndef(e.target.value) })}
            />
          </div>

          <div className="space-y-2">
            <Label>Parking Spots</Label>
            <Input
              inputMode="numeric"
              value={property.parkingSpots ?? ''}
              onChange={(e) => patch({ parkingSpots: toNumOrUndef(e.target.value) })}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
